import JsCookies from 'js-cookie';
import type { RequestParameters, RequestReturnType } from '@shopware/api-client';
// @ts-ignore
import type { operationPaths, operations, components } from '@shopware/api-client/api-types/apiTypes-6.5.3.0.js';
import { PUBLIC_API_BASE_URL, PUBLIC_SW_ACCESS_KEY } from '$env/static/public';
import { transformPathToQuery } from '$lib/api/util/transformPathToQuery.js';
import { ApiClientError } from '$lib/api/util/errorInterceptor.js';
import { browser } from '$app/environment';
import { stringify } from 'qs';
import type { ShopContext } from '$lib/context.js';
import type { Cookies } from '@sveltejs/kit';
import { DateTime } from 'luxon';

export type ApiSchemas = components['schemas'];
// reimport operations request parameters to use it in application
export type ApiRequestParams<OPERATION_NAME extends keyof operations> = RequestParameters<OPERATION_NAME, operations>;
// reimport operations return types to use it in application
export type ApiReturnType<OPERATION_NAME extends keyof operations> = RequestReturnType<OPERATION_NAME, operations>;

export function createAPIClient<OPERATIONS = operations, PATHS = operationPaths>(config: {
    baseURL: string;
    /**
     * @deprecated this method is only for store-api, for admin API use `createAdminAPIClient`. Remove this param
     */
    apiType?: 'store-api' | 'admin-api';
    accessToken: string;
}) {
    /**
     * Invoke API request based on provided path definition.
     */
    async function invoke<
        INVOKE_PATH extends PATHS,
        OON = INVOKE_PATH extends `${infer R} ${string}` ? R : never,
        OPERATION_NAME extends keyof OPERATIONS = OON extends keyof OPERATIONS ? OON : never
    >(
        pathParam: INVOKE_PATH extends string ? INVOKE_PATH : never,
        params: RequestParameters<OPERATION_NAME, OPERATIONS>,
        {
            fetchImplementation,
            shopContext,
            headers,
            ssrCookies,
            returnRawResponse
        }: {
            fetchImplementation?: typeof fetch;
            shopContext: ShopContext;
            headers?: Record<string, string>;
            ssrCookies?: Cookies;
            returnRawResponse?: boolean;
        }
    ): Promise<RequestReturnType<OPERATION_NAME, OPERATIONS>> {
        const [requestPath, options] = transformPathToQuery(pathParam, params as Record<string, string>);

        const fetchFunc = fetchImplementation ?? fetch;
        //console.log('fetching', pathParam, params, fetchImplementation ? 'using ssr fetch' : 'using browser fetch');

        let response: Response | undefined;

        try {
            const swContextToken = browser ? JsCookies.get('sw-context-token') : shopContext.storeContext.token;

            response = await fetchFunc(
                config.baseURL +
                requestPath +
                (options.query ? '?' + stringify(options.query, { arrayFormat: 'repeat' }) : ''),
                {
                    method: options.method,
                    body: JSON.stringify(options.body) as any,
                    headers: new Headers({
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'sw-access-key': config.accessToken,
                        ...options.headers,
                        ...(swContextToken
                            ? {
                                'sw-context-token': swContextToken
                            }
                            : {}),
                        ...(shopContext.shopwareLanguage.languageId
                            ? { 'sw-language-id': shopContext.shopwareLanguage.languageId }
                            : {}),
                        ...headers
                    } as Record<string, string>)
                }
            );
        } catch (error) {
            console.log(JSON.stringify(error,null,2))
            throw new ApiClientError();
        }

        if (returnRawResponse) {
            return response as any;
        }

        let data: any;

        try {
            data = await response.json();
        } catch (error) {
            /* empty */
        }

        if (!response?.ok) {
            throw new ApiClientError(response, data);
        }

        if (response.headers.has('sw-context-token')) {
            const newContextToken = response.headers.get('sw-context-token')!;

            if (browser && JsCookies.get('sw-context-token') !== newContextToken) {
                JsCookies.set('sw-context-token', newContextToken, {
                    expires: 365, // days
                    path: '/',
                    sameSite: 'lax',
                    secure: false,
                    httpOnly: false
                });
            } else if (ssrCookies && ssrCookies.get('sw-context-token') !== newContextToken) {
                ssrCookies.set('sw-context-token', newContextToken, {
                    expires: DateTime.now().plus({ days: 365 }).toJSDate(), // days
                    path: '/',
                    sameSite: 'lax',
                    secure: false,
                    httpOnly: false
                });
            }
        }

        return data;
    }

    return {
        invoke
    };
}

export const shopwareApiClient = createAPIClient<operations, operationPaths>({
    baseURL: PUBLIC_API_BASE_URL,
    accessToken: PUBLIC_SW_ACCESS_KEY
});
